import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API, axios } from "@/api";
import {
  Gift, Star, Loader2, Bookmark, Sparkles, PartyPopper, Eye,
} from "lucide-react";
import { motion } from "framer-motion";

export default function SharedWishlistPage() {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/wishlist/shared/${shareId}`);
        setData(res.data);
        // Set OG meta tags dynamically for in-app rendering
        document.title = `${res.data.user_name}'s Birthday Wishlist — $${res.data.total_value?.toFixed(0)} in Free Stuff`;
        const setMeta = (prop, content) => {
          let el = document.querySelector(`meta[property="${prop}"]`) || document.querySelector(`meta[name="${prop}"]`);
          if (!el) { el = document.createElement("meta"); el.setAttribute(prop.startsWith("og:") ? "property" : "name", prop); document.head.appendChild(el); }
          el.setAttribute("content", content);
        };
        setMeta("og:title", document.title);
        setMeta("og:description", `${res.data.item_count} birthday deals saved`);
        setMeta("og:image", `${API}/wishlist/shared/${shareId}/og-image`);
        setMeta("og:image:width", "1200");
        setMeta("og:image:height", "630");
        setMeta("og:url", window.location.href);
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", document.title);
        setMeta("twitter:image", `${API}/wishlist/shared/${shareId}/og-image`);
      } catch { setError("This wishlist link is no longer available."); }
      finally { setLoading(false); }
    })();
  }, [shareId]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-violet-400" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-6 text-center">
      <PartyPopper className="w-12 h-12 text-slate-300 mb-4" />
      <p className="text-lg font-bold text-slate-700">{error}</p>
      <button
        onClick={() => navigate("/")}
        className="mt-4 px-6 py-2.5 bg-violet-500 text-white text-sm font-bold rounded-xl"
        data-testid="shared-wishlist-go-home"
      >
        Build your own wishlist!
      </button>
    </div>
  );

  const items = data.wishlist_snapshot || [];
  const windowLabel = (type) => {
    if (!type) return "";
    const map = { birthday_only: "Birthday only", birthday_week: "Birthday week", birthday_month: "Birthday month", any_time: "Any time" };
    return map[type] || type.replace(/_/g, " ");
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-12" data-testid="shared-wishlist-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-violet-500 to-indigo-600 px-4 py-6 text-white text-center">
        <p className="text-[10px] font-bold text-violet-200 uppercase tracking-wider">
          {data.user_name}'s Birthday Wishlist
        </p>
        <p className="text-3xl font-black mt-1">${data.total_value.toFixed(0)} in Birthday Deals</p>
        <div className="flex justify-center gap-6 mt-3">
          <div>
            <p className="text-2xl font-black">{data.item_count}</p>
            <p className="text-[10px] text-violet-200">deals</p>
          </div>
          <div>
            <p className="text-2xl font-black">{data.view_count}</p>
            <p className="text-[10px] text-violet-200">views</p>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="px-4 pt-4 space-y-2">
        {items.map((item, i) => (
          <motion.div
            key={item.business_id || i}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="bg-white rounded-xl border border-slate-200/80 p-3.5 flex items-center gap-3"
            data-testid={`shared-wishlist-item-${item.business_id || i}`}
          >
            <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center overflow-hidden border border-violet-100 flex-shrink-0">
              {item.logo_url ? (
                <img src={item.logo_url} alt="" className="w-full h-full object-contain p-0.5" onError={e => e.target.style.display = "none"} />
              ) : (
                <Gift className="w-5 h-5 text-violet-300" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-slate-800 truncate">{item.name || item.business_name}</h3>
              <p className="text-xs text-slate-500 truncate mt-0.5">{item.offer_title}</p>
              <div className="flex items-center gap-2 mt-1.5">
                {item.offer_value_est > 0 && (
                  <span className="text-xs font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">${item.offer_value_est}</span>
                )}
                {item.birthday_window_type && (
                  <span className="text-[10px] text-slate-400">{windowLabel(item.birthday_window_type)}</span>
                )}
                {item.ease_stars > 0 && (
                  <span className="flex items-center gap-0.5 text-[10px] text-slate-400">
                    <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" />{item.ease_stars}
                  </span>
                )}
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* CTA */}
      <div className="px-4 pt-8 text-center">
        <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-2xl p-6 border border-violet-200">
          <Bookmark className="w-8 h-8 text-violet-500 mx-auto mb-3" />
          <p className="text-lg font-bold text-slate-800">Build Your Own Birthday Wishlist</p>
          <p className="text-sm text-slate-500 mt-1">Find hundreds of birthday deals near you — totally free.</p>
          <button
            onClick={() => navigate("/")}
            className="mt-4 px-8 py-3 bg-gradient-to-r from-violet-500 to-indigo-600 text-white text-sm font-bold rounded-xl shadow-lg shadow-violet-200 hover:shadow-violet-300 transition-shadow"
            data-testid="shared-wishlist-signup-cta"
          >
            <Sparkles className="w-4 h-4 inline mr-2" />Get Started Free
          </button>
        </div>
        <p className="text-[9px] text-slate-400 mt-3 flex items-center justify-center gap-1">
          <Eye className="w-3 h-3" />{data.view_count} people have viewed this wishlist
        </p>
      </div>
    </div>
  );
}
